import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardPanel, 
  IconHighlight, 
  Avatar, 
  Typography, 
  Button,
  Icon,
  Skeleton
} from "@ann/web-components";
import { getContactsList, getContactPreview } from '../../services/contacts';
import VehiclesChatTool from './VehiclesChatTool';
import { useStore } from '../../hooks';
import { TextField } from '@mui/material';
import './ContactsChatTool.css';

/**
 * Chat tool that displays contacts when "contacts" is typed in chat
 */
const ContactsChatTool = observer(() => {
  const { authStore } = useStore();
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const navigate = useNavigate();

  // Load contacts with search and pagination
  const loadContacts = async (page = 1, search = '') => {
    try {
      setIsLoading(true);
      const response = await getContactsList({
        page: page,
        page_size: 10, // Show 10 contacts per page in chat
        order_by: ['legalName'],
        order_direction: ['ASC'],
        include_deleted: false,
        search: search || null,
        filters: null
      });
      
      console.log('📞 UCP Contacts loaded:', response);
      setContacts(Array.isArray(response.body) ? response.body : []);
      setTotalContacts(response.meta?.totalItems || 0);
    } catch (err) {
      console.error('Error loading UCP contacts:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContacts(1, ''); // Load initial contacts
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search
      loadContacts(1, searchTerm);
    }, 500);
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm]);

  const filteredContacts = contacts || [];

  const handleSearch = (value) => {
    // Ensure value is always a string
    const searchValue = typeof value === 'string' ? value : (value?.target?.value || '');
    console.log('🔍 Search value:', searchValue, typeof searchValue);
    setSearchTerm(searchValue);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadContacts(page, searchTerm);
  };

  const handleContactClick = async (contact) => {
    setSelectedContact(contact);
    setLoadingDetails(true);
    
    try {
      const response = await getContactPreview(contact.partyUuid);
      setContactDetails(response.body || contact);
    } catch (error) {
      console.error('Error fetching contact details:', error);
      setContactDetails(contact); // Fall back to basic contact info
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewContact = (contactId) => {
    navigate(`/v2/ucp/${contactId}/overview`);
  };

  const handleBackToList = () => {
    setSelectedContact(null);
  };

  if (isLoading) {
    return (
      <div className="contacts-chat-tool">
        <div className="contacts-header">
          <Typography category='title' weight='bold' size='large'>Contacts</Typography>
          <Typography category='body' weight='regular' size='small'>Loading contacts...</Typography>
        </div>
        <div className="contacts-loading">
          <Skeleton height="60px" width="100%" style={{ marginBottom: '12px' }} />
          <Skeleton height="60px" width="100%" style={{ marginBottom: '12px' }} />
          <Skeleton height="60px" width="100%" />
        </div>
      </div>
    );
  }

  if (selectedContact) {
    return (
      <div className="contacts-chat-tool">
        <div className="preview-header">
          <Button 
            size="small" 
            type="ghost" 
            icon="arrowLeft" 
            onClick={handleBackToList}
          />
          <Typography category='body' weight='regular' size='small'>Overview of this contact</Typography>
        </div>

        {/* Contact Information Card - Same as UCP */}
        <Card size='large' style={{ marginBottom: '16px' }}>
          <CardPanel isExpandable={true} openDefault={true}>
            <CardPanel.Header title='Contact information'>
              <CardPanel.Header.LeftSlot>
                <div className="header-left-slot">
                  <Icon name='dragIndicator' size='medium' />
                  <IconHighlight icon='person' bgColor='primary' iconColor='primary' size='medium' />
                </div>
              </CardPanel.Header.LeftSlot>
            </CardPanel.Header>
            
            <CardPanel.Content>
              {loadingDetails ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <Skeleton height="120px" width="100%" />
                </div>
              ) : (
                <div className="contact-widget-container">
                  <div className="contact-top">
                    <Avatar 
                      src={`https://i.pravatar.cc/56?u=${selectedContact.partyUuid}`}
                      height={56} 
                      width={56} 
                    />
                    <div className="contact-top-info">
                      <Typography category='title' weight='bold' size='large'>
                        {contactDetails?.legalName || selectedContact.legalName || 'Unknown Name'}
                      </Typography>
                      <Typography category='body' weight='regular' size='small'>
                        Contact Information
                      </Typography>
                    </div>
                  </div>
                  
                  <div className="contact-bottom">
                    <div className="contact-details">
                      {(contactDetails?.phone || selectedContact.phone) && (
                        <div className="detail-item">
                          <Icon name='sms' size='small' />
                          <Typography category='body' weight='regular' size='small'>
                            {contactDetails?.phone || selectedContact.phone}
                          </Typography>
                        </div>
                      )}
                      
                      {(contactDetails?.email || selectedContact.email) && (
                        <div className="detail-item">
                          <Icon name='inbox' size='small' />
                          <Typography category='body' weight='regular' size='small'>
                            {contactDetails?.email || selectedContact.email}
                          </Typography>
                        </div>
                      )}
                      
                      {contactDetails?.address && (
                        <div className="detail-item">
                          <Icon name='locationOn' size='small' />
                          <Typography category='body' weight='regular' size='small'>
                            {[
                              contactDetails.address.addressLine,
                              contactDetails.address.addressCity,
                              contactDetails.address.addressState
                            ].filter(Boolean).join(', ')}
                          </Typography>
                        </div>
                      )}
                      
                      {contactDetails?.address?.addressPostalCode && (
                        <div className="detail-item">
                          <Icon name='tag' size='small' />
                          <Typography category='body' weight='regular' size='small'>
                            {contactDetails.address.addressPostalCode}
                          </Typography>
                        </div>
                      )}
                    </div>
                    
                    <div className="contact-actions">
                      <Button
                        label="View Contact"
                        size="medium"
                        type="primary"
                        onClick={() => handleViewContact(selectedContact.partyUuid)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardPanel.Content>
          </CardPanel>
        </Card>

        {/* Vehicle Information Card - Same as UCP */}
        <VehiclesChatTool contactId={selectedContact.partyUuid} />
      </div>
    );
  }

  return (
    <div className="contacts-chat-tool">
      <div className="contacts-header">
        <Typography category='title' weight='bold' size='large'>Contacts</Typography>
        <Typography category='body' weight='regular' size='small'>
          {totalContacts} contact{totalContacts !== 1 ? 's' : ''} found
        </Typography>
      </div>

      <div className="contacts-search">
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, company, or phone..."
          value={searchTerm}
          onChange={handleSearch}
          variant="outlined"
        />
      </div>

      <div className="contacts-list">
        {filteredContacts.length === 0 ? (
          <div className="no-contacts">
            <Typography category='body' weight='regular' size='small'>No contacts found</Typography>
          </div>
        ) : (
          <>
            {filteredContacts.map((contact) => (
              <ContactListItem 
                key={contact.partyUuid}
                contact={contact}
                onClick={() => handleContactClick(contact)}
              />
            ))}
            
            {/* Pagination */}
            {Math.ceil(totalContacts / 10) > 1 && (
              <div className="contacts-pagination">
                <Button
                  size="small"
                  type="ghost"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                <Typography category='body' weight='regular' size='small'>
                  Page {currentPage} of {Math.ceil(totalContacts / 10)}
                </Typography>
                <Button
                  size="small"
                  type="ghost"
                  disabled={currentPage >= Math.ceil(totalContacts / 10)}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

/**
 * Individual contact item in the list
 */
const ContactListItem = ({ contact, onClick }) => (
  <div className="contact-list-item" onClick={onClick}>
    <div className="contact-avatar">
      <Avatar 
        src={`https://i.pravatar.cc/40?u=${contact.id}`}
        height={40}
        width={40}
      />
    </div>
    <div className="contact-info">
      <Typography category='body' weight='bold' size='medium'>
        {contact.legalName || 'Unknown Name'}
      </Typography>
      <Typography category='body' weight='regular' size='small'>
        {contact.address?.addressCity || 'No location'}
      </Typography>
    </div>
    <div className="contact-details">
      <Typography category='body' weight='regular' size='small'>
        {contact.phone || 'No phone'}
      </Typography>
    </div>
  </div>
);

/**
 * Contact preview card that matches the screenshot style
 */
const ContactPreviewCard = observer(({ contact, onBack, onView }) => {
  const fullAddress = [
    contact.address?.addressLine,
    contact.address?.addressCity,
    contact.address?.addressState
  ].filter(Boolean).join(', ');

  return (
    <div className="contact-preview">
      <div className="preview-header">
        <Button 
          size="small" 
          type="ghost" 
          icon="arrowLeft" 
          onClick={onBack}
        />
        <Typography category='body' weight='regular' size='small'>Contact Preview</Typography>
      </div>

      <Card size='large'>
        <CardPanel isExpandable={false} openDefault={true}>
          <CardPanel.Header title='Contact information'>
            <CardPanel.Header.LeftSlot>
              <div className="header-left-slot">
                <Icon name='dragIndicator' size='medium' />
                <IconHighlight icon='person' bgColor='primary' iconColor='primary' size='medium' />
              </div>
            </CardPanel.Header.LeftSlot>
          </CardPanel.Header>
          <CardPanel.Content>
            <div className="contact-widget-container">
              <div className="contact-top">
              <Avatar 
                src={`https://i.pravatar.cc/56?u=${contact.partyUuid}`}
                height={56} 
                width={56} 
              />
              <div className="contact-top-info">
                <Typography category='title' weight='bold' size='large'>
                  {contact.legalName || 'Unknown Name'}
                </Typography>
                <Typography category='body' weight='regular' size='small'>
                  Contact Information
                </Typography>
              </div>
            </div>
            
            <div className="contact-bottom">
              <div className="contact-details">
                {contact.phone && (
                  <div className="detail-item">
                    <Icon name='sms' size='small' />
                    <Typography category='body' weight='regular' size='small'>
                      {contact.phone}
                    </Typography>
                  </div>
                )}
                
                {contact.email && (
                  <div className="detail-item">
                    <Icon name='inbox' size='small' />
                    <Typography category='body' weight='regular' size='small'>
                      {contact.email}
                    </Typography>
                  </div>
                )}
                
                {fullAddress && (
                  <div className="detail-item">
                    <Icon name='locationOn' size='small' />
                    <Typography category='body' weight='regular' size='small'>
                      {fullAddress}
                    </Typography>
                  </div>
                )}
                
                {contact.address?.addressPostalCode && (
                  <div className="detail-item">
                    <Icon name='tag' size='small' />
                    <Typography category='body' weight='regular' size='small'>
                      {contact.address.addressPostalCode}
                    </Typography>
                  </div>
                )}
                </div>
                
                <div className="contact-actions">
                <Button
                  label="View Contact"
                  size="medium"
                  type="primary"
                  onClick={() => onView(contact.partyUuid)}
                />
                </div>
              </div>
            </div>
          </CardPanel.Content>
        </CardPanel>
      </Card>
    </div>
  );
});

export default ContactsChatTool;
export { ContactPreviewCard, ContactListItem };
